import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PettyCashService } from '../../features/petty-cash/services/petty-cash.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { noWhitespaceValidator } from '../../shared/services/validations';
import { EmployeeService } from '../../features/employees/employee.service';
import { CurrencyAdjustmentService } from '../../features/currency-adjustment/services/currency-adjustment.service';

@Component({
  selector: 'app-petty-cash-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    FormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './petty-cash-new.component.html',
  styleUrl: './petty-cash-new.component.scss',
})
export class PettyCashNewComponent {
  addPettyCashActivityForm: FormGroup;
  bankAccountsData: any[] = [];
  employeesData: any[] = [];
  selectedEmployeeId: any;
  employeePettyCashStatus = false;
  private searchSubject = new Subject<string>();
  private employeeSearchSubject = new Subject<string>();
  currencyData: any;
  employeeId: any;
  pettyCashId: any;
  activityId: any;
  currentBalance: any;
  loading = false;
  pettyCashActivityData: any;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private route: Router,
    private router: ActivatedRoute,
    private pettyCashService: PettyCashService,
    private employeeService: EmployeeService,
    private currencyService: CurrencyAdjustmentService
  ) {}

  ngOnInit() {
    this.initForm();
    this.getAccountsType();
    this.getAllEmployees();
    this.fetchCurrencies();
    this.addPettyCashActivityForm
      .get('employeeId')
      ?.valueChanges.subscribe((employeeId) => {
        if (employeeId) {
          this.selectedEmployeeId = employeeId;
          this.fetchEmployeePettyCashStatus(employeeId);
          this.searchSubject.next('');
        }
      });
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAccountsType(searchTerm);
      });
      this.employeeSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllEmployees(searchTerm);
      });
    this.router.queryParamMap.subscribe((queryParams) => {
      this.employeeId = queryParams.get('employeeId');
      this.pettyCashId = queryParams.get('pettyCashId');
      this.activityId = queryParams.get('activityId');
      if (this.employeeId) {
        this.addPettyCashActivityForm
          .get('employeeId')
          ?.setValue(Number(this.employeeId));
        this.fetchEmployeePettyCashStatus(Number(this.employeeId));
        this.addPettyCashActivityForm.get('employeeId')?.disable();
        // if (this.activityId) {
        //   this.getActivityById(this.activityId);
        // }
      }
    });
  }

  initForm() {
    if (this.pettyCashActivityData) {
      this.addPettyCashActivityForm = this.fb.group({
        employeeId: [{ value: Number(this.employeeId), disabled: true }],
        type: [this.pettyCashActivityData?.type, [Validators.required]],
        description: [
          this.pettyCashActivityData?.description,
          [Validators.required, noWhitespaceValidator()],
        ],
        accountId: [
          Number(this.pettyCashActivityData?.accountId),
          [Validators.required],
        ],
        amount: [
          Number(this.pettyCashActivityData?.amount),
          [Validators.required, Validators.min(0.01)],
        ],
        currencyId: [null],
        pettyCashNumber: [''],
      });
    } else {
      this.addPettyCashActivityForm = this.fb.group({
        employeeId: ['', [Validators.required]],
        type: ['', [Validators.required]],
        description: ['', [Validators.required, noWhitespaceValidator()]],
        accountId: ['', [Validators.required]],
        amount: [null, [Validators.required,  Validators.min(0.01)]],
        currencyId: [null, [Validators.required]],
        pettyCashNumber: ['', [Validators.required]],
      });
    }
  }

  fetchEmployeePettyCashStatus(employeeId: any) {
    this.pettyCashService.employeeHasPettyCash(employeeId).subscribe({
      next: (res) => {
        this.currentBalance = res.currentBalance;
        this.employeePettyCashStatus = res.exists;
        this.updateValidationsByEmployeeStatus(this.employeePettyCashStatus);
        if (this.activityId) {
          this.getActivityById(this.activityId);
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  updateValidationsByEmployeeStatus(status: Boolean) {
    if (status === false) {
      this.addPettyCashActivityForm.get('type')?.setValue('IN');
      this.addPettyCashActivityForm.get('type')?.disable();
      this.addPettyCashActivityForm
        .get('currencyId')
        ?.setValidators([Validators.required]);
      this.addPettyCashActivityForm
        .get('pettyCashNumber')
        ?.setValidators([Validators.required]);
      this.addPettyCashActivityForm.get('currencyId')?.updateValueAndValidity();
      this.addPettyCashActivityForm
        .get('pettyCashNumber')
        ?.updateValueAndValidity();
    } else {
      this.addPettyCashActivityForm.get('type')?.setValue('');
      this.addPettyCashActivityForm.get('type')?.enable();
      this.addPettyCashActivityForm.get('type')?.markAsUntouched();

      this.addPettyCashActivityForm.get('currencyId')?.clearValidators();
      this.addPettyCashActivityForm.get('pettyCashNumber')?.clearValidators();
      this.addPettyCashActivityForm.get('currencyId')?.updateValueAndValidity();
      this.addPettyCashActivityForm
        .get('pettyCashNumber')
        ?.updateValueAndValidity();
    }
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onSearchEmployee(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.employeeSearchSubject.next(searchTerm);
  }
  getAccountsType(search: string = '') {
    this.pettyCashService.findAccountsByMultipleTypes([], search).subscribe({
      next: (res) => {
        this.bankAccountsData = res;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getActivityById(id: any) {
    this.pettyCashService.fetchPettyCashActivityById(Number(id)).subscribe({
      next: (res) => {
        this.pettyCashActivityData = res;
        this.initForm();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getAllEmployees(search: string = '') {
    this.employeeService.getEmployeeApprovers(search).subscribe({
      next: (res) => {
        this.employeesData = res;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  fetchCurrencies() {
    this.currencyService.fetchCurrencies('', {}).subscribe({
      next: (currency) => {
        this.currencyData = currency;
      },
      error: (error) => console.error(error),
    });
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  onSave() {
    if (this.addPettyCashActivityForm.invalid) {
      this.addPettyCashActivityForm.markAllAsTouched();
      return;
    }
    const formValue = this.addPettyCashActivityForm.value;
    const branchId = localStorage.getItem('selectedBranchId');
    const pettyCashInput = {
      branchId: Number(branchId),
      employeeId: Number(formValue.employeeId),
      pettyCashNumber: formValue.pettyCashNumber,
      currencyId: Number(formValue.currencyId),
      openingBalance: parseFloat(formValue.amount),
    };

    const activityInput: any = {
      type: formValue.type || 'IN',
      amount: parseFloat(formValue.amount),
      accountId: Number(formValue.accountId),
      description: formValue.description,
      ...(this.employeePettyCashStatus
        ? {
            employeeId: Number(formValue.employeeId) || Number(this.employeeId),
          }
        : { pettyCashNumber: formValue.pettyCashNumber }),
    };

    const updateActivityInput = {
      ...(this.activityId ? { id: Number(this.activityId) } : {}),
      cashAccountId: Number(this.pettyCashId),
      type: formValue.type,
      description: formValue.description,
      accountId: Number(formValue.accountId),
      amount: parseFloat(formValue.amount),
    };
    if (this.activityId) {
      this.pettyCashService
        .updatePettyCashActivity(updateActivityInput, Number(this.activityId))
        .subscribe({
          next: (res) => {
            this.toastr.success('Petty cash activity successfully updated');
            this.onCancel();
          },
          error: (err) => {
            this.toastr.error(err?.message || 'Error in updating activity');
          },
        });
    } else {
      if (!this.employeePettyCashStatus) {
        this.loading = true;
        this.pettyCashService
          .createPettyCashAccount(pettyCashInput, activityInput)
          .subscribe({
            next: (res) => {
              this.loading = false;
              this.toastr.success('Petty cash account created successfull');
              this.onCancel();
            },
            error: (err) => {
              this.loading = false;
              console.log(err);
              this.toastr.error(
                err?.message || 'Failed to create petty cash account'
              );
            },
          });
      } else {
        this.loading = true;
        this.pettyCashService.createPettyCashActivity(activityInput).subscribe({
          next: (res) => {
            this.loading = false;
            this.toastr.success('Petty cash activity created successfull');
            this.onCancel();
          },
          error: (err) => {
            this.loading = false;
            console.log(err);
            this.toastr.error(
              err?.message || 'Failed to create petty cash activity'
            );
          },
        });
      }
    }
  }

  onCancel() {
    this.route.navigate(['petty-cash/petty-cash-details'], {
      queryParams: {
        pettyCashId: this.pettyCashId,
        employeeId: this.employeeId,
      },
    });
  }
}
